import { useState, useEffect, useCallback } from 'react'
import { muxConfig } from '../../config/mux.config'

interface ThumbnailSelectorProps {
  playbackId: string
  duration: number
  currentTime: number | null
  onChange: (time: number) => void
  disabled?: boolean
}

/**
 * ThumbnailSelector - Allows users to select a video thumbnail by scrubbing through the timeline
 *
 * Uses Mux's dynamic thumbnail generation via URL parameters for instant preview.
 * No image upload or processing needed - just stores the timestamp.
 */
export function ThumbnailSelector({
  playbackId,
  duration,
  currentTime,
  onChange,
  disabled = false,
}: ThumbnailSelectorProps) {
  // Default to 25% of duration if no time selected
  const defaultTime = duration ? duration * 0.25 : 0
  const [selectedTime, setSelectedTime] = useState<number>(currentTime ?? defaultTime)
  const [isLoading, setIsLoading] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Update selected time when prop changes (e.g., loading saved value)
  useEffect(() => {
    if (currentTime !== null) {
      setSelectedTime(currentTime)
    }
  }, [currentTime])

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Debounce the onChange callback to avoid too many updates
  const debouncedOnChange = useCallback(
    debounce((time: number) => {
      onChange(time)
    }, 300),
    [onChange]
  )

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value)
    setSelectedTime(newTime)
    setIsLoading(true)
    setImageError(false)
    debouncedOnChange(newTime)
  }

  // Get thumbnail URL for current selection
  const thumbnailUrl = muxConfig.getThumbnailUrl(playbackId, {
    width: 640,
    height: 360,
    time: selectedTime,
  })

  // Handle image load
  const handleImageLoad = () => {
    setIsLoading(false)
  }

  // Handle image error
  const handleImageError = () => {
    setIsLoading(false)
    setImageError(true)
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        Thumbnail
      </label>

      {/* Thumbnail Preview */}
      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
        {imageError ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <svg
                className="mx-auto h-10 w-10 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-sm">Failed to load thumbnail</p>
              <button
                onClick={() => {
                  setImageError(false)
                  setIsLoading(true)
                }}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            <img
              src={thumbnailUrl}
              alt="Video thumbnail preview"
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                isLoading ? 'opacity-50' : 'opacity-100'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Time Slider */}
      <div className="space-y-2">
        <input
          type="range"
          min={0}
          max={duration || 1}
          step={0.1}
          value={selectedTime}
          onChange={handleSliderChange}
          disabled={disabled || !duration}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        {/* Time Labels */}
        <div className="flex justify-between text-xs text-gray-500">
          <span>0:00</span>
          <span className="font-medium text-gray-700">
            {formatTime(selectedTime)}
          </span>
          <span>{formatTime(duration || 0)}</span>
        </div>
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        Drag the slider to select the frame for your video thumbnail
      </p>
    </div>
  )
}

// Simple debounce utility
function debounce<T extends unknown[]>(
  func: (...args: T) => void,
  wait: number
): (...args: T) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: T) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}
